// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import activeRadio from "../../../../../../assets/images/radio_active.png";
import inactiveRadio from "../../../../../../assets/images/radio_inactive.png";
import Button from "../../../../../../components/ui/Button";
import { getProjects, getProject } from "../../../../../../api";
import { saveStory, getStoryById } from "../../../../../../api/museum";
import AudioPlayerRewind from "../../../../../../components/common/AudioPlayer";
import Dropdown from "../../../../../../components/common/Dropdown";
import { FileInput } from "../../../../../../components/common/FileInput";
import HorizontalSeparator from "../../../../../../components/common/HorizontalSeparator";
import RadioButton from "../../../../../../components/common/RadioButton";
import TitleSubtitle from "../../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../../../context/useGlobalState";
import { uploadFileToS3Complete } from "../../../../../../api/s3";
import { StoryAudioProps, RouteParams, DropdownOption } from "../../../../../../types/screensTypes";
import { idToLanguage } from "../../../../../../utils/idtolanguage";
import stepStyles from "../../../createStep.module.css";
import audioFooter from "../../../../../../components/common/AudioPlayerFooter.module.css";

const StoryAudio: React.FC<StoryAudioProps> = ({ navigateToNext }) => {
    const { storyId, planId, language } = useParams<RouteParams>();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();
    const { trackData } = useTrackCreation();

    // Mode for input: 'manual' for file upload, 'project' for selecting from projects.
    const [mode, setMode] = useState<"manual" | "project">("manual");

    const [mp3File, setMp3File] = useState<string | null>(null);
    const [projects, setProjects] = useState<DropdownOption[]>([]);
    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [projectData, setProjectData] = useState<any>(null);

    // Format language options from global state.
    const languagesAIOptions: DropdownOption[] = data?.languagesAI?.map((language) => ({
        value: language.code,
        label: language.label,
    }));

    // Filtered audio options based on project data.
    const filteredAudioOptions: DropdownOption[] =
        projectData?.audioAI
            ?.filter((audio: any) => audio.language === selectedContentLanguage)
            .map((audio: any) => ({
                value: audio.audioUrl,
                label: audio.name,
            })) || [];

    // Fetch projects for selection in project mode.
    const fetchProjects = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token) return;
        try {
            const response = await getProjects({ token });
            if (response) {
                const projectsDropdown = response.map((project: any) => ({
                    label: project.name,
                    value: project.id,
                }));
                setProjects(projectsDropdown);
            }
        } catch (err) {
            console.error("Failed to fetch projects:", err);
        }
    };

    // Fetch details for a selected project.
    const fetchProject = async (id: string): Promise<void> => {
        if (!id) return;
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await getProject({ token, projectId: id });
        if (response) {
            setProjectData(response);
        }
    };

    // Save story with the current audio file.
    const saveStoryAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!trackId || !token || !language || !storyId || !mp3File || !planId) return;
        const tempWpt = await saveStory({
            token,
            trackId,
            clickableZoneId: storyId,
            subplanId: planId,
            language,
            audio: mp3File,
        });
        if (tempWpt && tempWpt.clickableZoneId) {
            const id: string = tempWpt.clickableZoneId;
            navigateToNext(id, language, planId);
        }
    };

    // Retrieve existing story audio.
    const getStoryAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!trackId || !token || !language || !storyId) return;
        const storyData = await getStoryById({
            token,
            trackId,
            clickableZoneId: storyId,
            language,
        });
        if (storyData) {
            const localizedData = storyData.clickableZonesLocalized;
            if (localizedData && localizedData.length > 0) {
                const audioFile = localizedData[0]?.audiotrack?.file;
                if (audioFile) {
                    setMp3File(audioFile);
                }
            }
        }
    };

    const handleFileSelected = (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files; // Toujours prendre un seul fichier

        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true, // Public files for general uploads
                    isFolder: false, // No folder structure for general uploads
                },
                token,
            )
                .then((result: string) => {
                    setMp3File(result);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    useEffect(() => {
        if (storyId && storyId !== "") {
            getStoryAPI();
        }
    }, [storyId, language]);

    useEffect(() => {
        if (mode === "project") {
            fetchProjects();
        }
    }, [mode]);

    useEffect(() => {
        if (selectedProject && selectedProject !== "") {
            fetchProject(selectedProject);
        }
    }, [selectedProject]);

    return (
        <div>
            <TitleSubtitle
                title={t("storyAudio") + idToLanguage(language || "", data?.languagesAI, t)}
                subtitle={""}
                layout="step"
            />
            <HorizontalSeparator />

            <div>
                <FileInput
                    onFileSelected={handleFileSelected}
                    placeholder={t("dragAndDropYourAudioFileHereOrClickToSelectOne")}
                />
            </div>
            {/* Mode selection radio group */}
            <div className="radio-text-bold" style={{ marginTop: "20px" }}>
                <RadioButton
                    label={t("addFromYourProjects")}
                    checked={mode === "project"}
                    onChange={() => setMode("project")}
                    activeImage={activeRadio}
                    inactiveImage={inactiveRadio}
                    center={true}
                />
            </div>

            {mode === "project" && (
                <div className="dropdown-row">
                    <Dropdown
                        label=""
                        value={selectedProject}
                        options={projects}
                        onChange={(value: string) => setSelectedProject(value)}
                        placeholder={t("selectProject")}
                        loading={false}
                        error={false}
                    />
                    <Dropdown
                        label=""
                        value={selectedContentLanguage}
                        options={languagesAIOptions}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("selectALanguage")}
                    />
                    {selectedProject && selectedContentLanguage && (
                        <Dropdown
                            label=""
                            options={filteredAudioOptions}
                            onChange={setMp3File}
                            loading={loading}
                            error={error ? true : false}
                            placeholder={t("selectAnAudio")}
                        />
                    )}
                </div>
            )}

            <div className={stepStyles.stepButton}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={async () => {
                        await saveStoryAPI();
                    }}
                />
            </div>

            {mp3File && mp3File !== "" && (
                <div className={audioFooter.section}>
                    <HorizontalSeparator />
                    <AudioPlayerRewind title="" src={mp3File} isAutoPlay={false} />
                </div>
            )}
        </div>
    );
};

export default StoryAudio;
